import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  Exercise,
  getExercises,
  deleteExercise,
  updateExercise,
  createExercise,
} from "../../services/exerciseService";
import { ExerciseCreateRequest, ExerciseType } from "../../types/exercise";

interface ExercisesState {
  exercises: Exercise[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalExercises: number;
}

const initialState: ExercisesState = {
  exercises: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalExercises: 0,
};

export const fetchExercises = createAsyncThunk(
  "exercises/fetchExercises",
  async (
    {
      page = 1,
      limit = 10,
      name = [],
      categoryName = [],
      programName = [],
      targetMuscleName = [],
    }: {
      page?: number;
      limit?: number;
      name?: string[];
      categoryName?: string[];
      programName?: string[];
      targetMuscleName?: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await getExercises(
        page,
        limit,
        name,
        categoryName,
        programName,
        targetMuscleName
      );
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to fetch exercises"
      );
    }
  }
);

export const deleteExerciseAsync = createAsyncThunk(
  "exercises/deleteExercise",
  async (id: number) => {
    await deleteExercise(id);
    return id;
  }
);

export const updateExerciseAsync = createAsyncThunk(
  "exercises/updateExercise",
  async (
    { id, data }: { id: number; data: Partial<Exercise> },
    { rejectWithValue }
  ) => {
    try {
      const updatedExercise = await updateExercise(id, {
        ...data,
        name: data.name || "",
        description: data.description || "",
        imageUrls: data.imageUrls || [],
        videoUrls:
          data.videoUrls
            ?.filter(
              (video): video is { url: string; thumbnail: string } =>
                video !== null
            )
            .map((video) => ({
              url: video.url || "",
              thumbnail: video.thumbnail || "",
            })) || [],
        workingSets: data.workingSets || [],
        mood: data.mood || [],
        gender: data.gender || [],
        fitnessLevel: data.fitnessLevel || [],
        fitnessGoal: data.fitnessGoal || [],
        priority: data.priority || "",
        primaryTargetMuscles:
          data.primaryTargetMuscles?.map(
            (muscle: { id: number }) => muscle.id
          ) || [],
        secondaryTargetMuscles:
          data.secondaryTargetMuscles?.map(
            (muscle: { id: number }) => muscle.id
          ) || [],
        programIds: data.programIds || [],
        categoryIds: data.categoryIds || [],
        equipmentIds: data.equipmentIds || [],
        howToPerformIds: data.howToPerformIds || [],
      });
      return updatedExercise;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to update exercise"
      );
    }
  }
);

export const createExerciseAsync = createAsyncThunk(
  "exercises/createExercise",
  async (
    exerciseData: ExerciseCreateRequest & {
      exerciseType: ExerciseType;
      gender: string[];
      mood: string[];
    },
    { rejectWithValue }
  ) => {
    try {
      const response = await createExercise({
        ...exerciseData,
        videoUrls: exerciseData.videoUrls.map((video) => ({
          url: video.url,
          thumbnail: video.thumbnail,
        })),
        workingSets: exerciseData.workingSets.map((set) => ({
          set: set.set,
          kg: set.kg?.toString(),
          reps: set.reps?.toString(),
          time: set.time?.toString(),
          meters: set.meters?.toString(),
          kilometers: set.kilometers?.toString(),
          minutes: set.minutes?.toString(),
        })),
      });
      return response;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data || "Failed to create exercise"
      );
    }
  }
);

const exercisesSlice = createSlice({
  name: "exercises",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchExercises.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchExercises.fulfilled, (state, action) => {
        state.loading = false;
        state.exercises = action.payload.data;
        state.currentPage = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalExercises = action.payload.total;
      })
      .addCase(fetchExercises.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(deleteExerciseAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteExerciseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.exercises = state.exercises.filter(
          (exercise) => exercise.id !== action.payload
        );
      })
      .addCase(deleteExerciseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || "Failed to delete exercise";
      })
      .addCase(updateExerciseAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateExerciseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const index = state.exercises.findIndex(
          (ex) => ex.id === action.payload.id
        );
        if (index !== -1) {
          state.exercises[index] = action.payload;
        }
      })
      .addCase(updateExerciseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(createExerciseAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createExerciseAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        state.exercises.push(action.payload);
      })
      .addCase(createExerciseAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default exercisesSlice.reducer;
